import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Play, 
  BookOpen, 
  Search,
  Filter,
  Clock,
  X,
  Camera,
  Video,
  Film,
  Download,
  Share2,
  ChevronLeft,
  ChevronRight,
  Lock,
  Image as ImageIcon,
  ExternalLink,
  Radio,
  Coins
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lazy loading image with skeleton placeholder
const LazyImage = ({ src, alt, className, onClick, 'data-testid': testId }) => {
  const [loaded, setLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const imgRef = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImgSrc(src);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [src]);

  return (
    <div ref={imgRef} className={className} onClick={onClick} data-testid={testId}>
      {!loaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      {imgSrc && (
        <img
          src={imgSrc}
          alt={alt}
          className="w-full h-full object-cover"
          onLoad={() => setLoaded(true)}
          style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
          decoding="async"
        />
      )}
    </div>
  );
};

// ======================== VIDEOS TAB ========================
const VideosSection = ({ user }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => { fetchVideos(); }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/library`);
      setVideos(response.data.videos || []);
    } catch (err) {
      setError('Failed to load library videos');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(videos.map(v => v.category).filter(Boolean))];
  const difficulties = [...new Set(videos.map(v => v.difficulty).filter(Boolean))];

  const filteredVideos = videos.filter(video => {
    const matchesSearch = !searchTerm || 
      video.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || video.category === categoryFilter;
    const matchesDifficulty = difficultyFilter === 'all' || video.difficulty === difficultyFilter;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    return null;
  };

  const getVideoThumbnail = (video) => {
    if (video.thumbnail_url) return video.thumbnail_url;
    return getYouTubeThumbnail(video.video_url);
  };

  const isDirectVideo = (url) => {
    if (!url) return false;
    return url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || 
           url.includes('s3.') || url.includes('amazonaws.com') || url.startsWith('/uploads/');
  };

  return (
    <>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="library-search"
          />
        </div>
        <div className="flex gap-3">
          {categories.length > 0 && (
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[170px]" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {difficulties.length > 0 && (
            <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
              <SelectTrigger className="w-[140px]" data-testid="difficulty-filter">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                {difficulties.map(diff => (
                  <SelectItem key={diff} value={diff}>{diff}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading && <div className="flex justify-center py-16"><div className="spinner" /></div>}
      {error && !loading && (
        <div className="text-center py-16">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={fetchVideos} variant="outline">Try Again</Button>
        </div>
      )}
      {!loading && !error && filteredVideos.length === 0 && (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold text-lg mb-2">
            {videos.length === 0 ? 'No videos yet' : 'No matching videos'}
          </h3>
          <p className="text-muted-foreground">
            {videos.length === 0 ? 'Check back soon for new training content!' : 'Try adjusting your search or filters.'}
          </p>
        </div>
      )}

      {!loading && !error && filteredVideos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos.map(video => (
            <Card 
              key={video.id} 
              className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => setSelectedVideo(video)}
              data-testid={`library-video-${video.id}`}
            >
              <div className="relative aspect-video bg-muted">
                {getVideoThumbnail(video) ? (
                  <div className="w-full h-full" style={{ backgroundImage: `url(${getVideoThumbnail(video)})`, backgroundSize: 'cover', backgroundPosition: video.thumbnail_position || 'center' }} />
                ) : isDirectVideo(video.video_url) ? (
                  <video
                    src={video.video_url.startsWith('/uploads/') ? `${API_URL}${video.video_url}` : video.video_url}
                    preload="metadata"
                    muted
                    playsInline
                    className="w-full h-full object-cover pointer-events-none"
                    onLoadedMetadata={(e) => { e.target.currentTime = Math.min(15, e.target.duration * 0.5); }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                    <Play className="w-12 h-12 text-primary/50" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-8 h-8 text-primary ml-1" />
                  </div>
                </div>
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Clock className="w-3 h-3" />{video.duration}
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg mb-2 line-clamp-2">{video.title}</h3>
                {video.description && <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{video.description}</p>}
                <div className="flex flex-wrap gap-2">
                  {video.category && <Badge variant="outline">{video.category}</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 pr-8">
                <DialogTitle className="font-display text-xl">{selectedVideo?.title}</DialogTitle>
                {selectedVideo?.description && <p className="text-muted-foreground text-sm mt-2">{selectedVideo.description}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedVideo?.category && <Badge variant="outline">{selectedVideo.category}</Badge>}
              {selectedVideo?.tags?.map((tag, i) => (
                <span key={i} className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">{tag}</span>
              ))}
            </div>
          </DialogHeader>
          <div className="aspect-video bg-black mt-4 relative">
            {selectedVideo && isDirectVideo(selectedVideo.video_url) ? (
              <>
                <video
                  src={selectedVideo.video_url.startsWith('/uploads/') ? `${API_URL}${selectedVideo.video_url}` : selectedVideo.video_url}
                  controls autoPlay className="w-full h-full" data-testid="video-player"
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  muted={!!selectedVideo.voice_over_url}
                />
                {selectedVideo.voice_over_url && (
                  <audio
                    src={selectedVideo.voice_over_url.startsWith('/uploads/') ? `${API_URL}${selectedVideo.voice_over_url}` : selectedVideo.voice_over_url}
                    autoPlay data-testid="voiceover-audio"
                  />
                )}
              </>
            ) : selectedVideo && getVideoEmbedUrl(selectedVideo.video_url) ? (
              <iframe
                src={getVideoEmbedUrl(selectedVideo.video_url)}
                title={selectedVideo.title}
                className="w-full h-full" frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen data-testid="video-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white"><p>Video not available</p></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ======================== PHOTOS TAB ========================
const PhotosSection = ({ user }) => {
  const [eventGroups, setEventGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxEvent, setLightboxEvent] = useState(null);
  const [fullPhotoUrl, setFullPhotoUrl] = useState('');
  const [expandingEvent, setExpandingEvent] = useState(null);
  const [photoSearch, setPhotoSearch] = useState('');
  const [creditSettings, setCreditSettings] = useState(null);
  const [userCredits, setUserCredits] = useState(user?.credits || 0);
  const isSubscriber = user?.subscription_status === 'active' || user?.role === 'admin' || user?.role === 'coach';
  const isTrial = user?.subscription_status === 'trial';
  const isExempt = user?.role === 'admin' || user?.role === 'coach';
  const TRIAL_LIMIT = 0;

  useEffect(() => { fetchPhotos(); fetchCreditSettings(); }, []);

  const fetchCreditSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/credit-settings`);
      setCreditSettings(res.data);
      setUserCredits(res.data.user_credits);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchPhotos(); }, []);

  const fetchPhotos = async (eventId = null) => {
    try {
      setLoading(true);
      const url = eventId 
        ? `${API_URL}/api/event-photos?event_id=${eventId}&per_event=200&limit=500`
        : `${API_URL}/api/event-photos?per_event=6`;
      const res = await axios.get(url);
      setEventGroups(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllForEvent = async (eventId) => {
    try {
      setExpandingEvent(eventId);
      const res = await axios.get(`${API_URL}/api/event-photos?event_id=${eventId}&limit=500`);
      const fullEvent = (res.data.events || [])[0];
      if (fullEvent) {
        setEventGroups(prev => prev.map(g => g.event_id === eventId ? { ...g, photos: fullEvent.photos, total_photos: fullEvent.total_photos } : g));
      }
    } catch (err) {
      console.error('Failed to load all photos for event:', err);
    } finally {
      setExpandingEvent(null);
    }
  };

  const openLightbox = async (photo, index, eventGroup) => {
    setLightboxIndex(index);
    setLightboxEvent(eventGroup);
    setLightboxOpen(true);
    setFullPhotoUrl('');
    try {
      const res = await axios.get(`${API_URL}/api/event-photos/${photo.id}/full`);
      setFullPhotoUrl(res.data.url);
    } catch {
      setFullPhotoUrl(photo.thumbnail_url || photo.url || '');
    }
  };

  const getPhotoUrl = (url) => url?.startsWith('/uploads') ? `${API_URL}${url}` : url;
  
  // Use thumbnail proxy for grid images - generates thumbnails on-demand
  const getThumbUrl = (photo) => {
    if (photo.thumbnail_url) return photo.thumbnail_url;
    return `${API_URL}/api/event-photos/${photo.id}/thumb`;
  };

  // For lightbox: photos from the currently active event group
  const lightboxPhotos = lightboxEvent ? lightboxEvent.photos : [];

  const navigateLightbox = async (dir) => {
    const nextIndex = (() => {
      const n = lightboxIndex + dir;
      if (n < 0) return lightboxPhotos.length - 1;
      if (n >= lightboxPhotos.length) return 0;
      return n;
    })();
    setLightboxIndex(nextIndex);
    setFullPhotoUrl('');
    const nextPhoto = lightboxPhotos[nextIndex];
    if (nextPhoto) {
      try {
        const res = await axios.get(`${API_URL}/api/event-photos/${nextPhoto.id}/full`);
        setFullPhotoUrl(res.data.url);
      } catch {
        setFullPhotoUrl(nextPhoto.thumbnail_url || '');
      }
    }
  };

  const handleDownload = async (photo) => {
    if (!isExempt) {
      const cost = creditSettings?.photo_download_credits || 10;
      if (userCredits < cost) {
        toast.error(`Not enough credits. Need ${cost}, you have ${userCredits}. Purchase more credits to download.`);
        return;
      }
      try {
        const res = await axios.post(`${API_URL}/api/library/download-photo/${photo.id}`);
        setUserCredits(res.data.credits_remaining);
        toast.success(`Photo downloaded! ${res.data.credits_used} credits used, ${res.data.credits_remaining} remaining.`);
        // Now trigger actual download
        const a = document.createElement('a');
        a.href = `${API_URL}${res.data.download_url}`;
        a.download = photo.filename || 'event-photo.jpg';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        const detail = err.response?.data?.detail || 'Download failed';
        toast.error(detail);
      }
    } else {
      // Admins/coaches download free
      const downloadUrl = `${API_URL}/api/event-photos/download/${photo.id}`;
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = photo.filename || 'event-photo.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleShare = (platform, photo) => {
    const pageUrl = window.location.origin + '/library';
    const text = `Check out this photo from ${photo.event_title} - Elevated Roping Media`;
    
    // Try native share first on mobile (works for all platforms including TikTok/Instagram)
    if (navigator.share && platform === 'native') {
      navigator.share({ title: photo.event_title, text, url: pageUrl }).catch(() => {});
      return;
    }

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
        break;
      case 'tiktok':
      case 'instagram':
        // These platforms don't support direct web sharing - download photo + copy text
        navigator.clipboard?.writeText(text + '\n' + pageUrl).then(() => {
          toast.success(`Link copied! Download the photo and share it on ${platform === 'tiktok' ? 'TikTok' : 'Instagram'}.`);
        }).catch(() => {
          toast.info(`Download the photo and share it on ${platform === 'tiktok' ? 'TikTok' : 'Instagram'}`);
        });
        // Also trigger download
        handleDownload(photo);
        break;
      default:
        break;
    }
  };

  if (!isSubscriber && !isTrial) {
    return (
      <div className="text-center py-16">
        <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-2">Subscribers Only</h3>
        <p className="text-muted-foreground mb-6">Subscribe to view and download event photos.</p>
        <Link to="/subscription">
          <Button size="lg" data-testid="subscribe-for-photos-btn">Get Access</Button>
        </Link>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-16"><div className="spinner" /></div>;

  if (eventGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <Camera className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-semibold text-lg mb-2">No event photos yet</h3>
        <p className="text-muted-foreground">Photos from events will appear here.</p>
      </div>
    );
  }

  const currentPhoto = lightboxPhotos[lightboxIndex];

  return (
    <>
      {/* Event Filter & Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <Select value={selectedEvent} onValueChange={(val) => {
          setSelectedEvent(val);
          if (val !== 'all') {
            loadAllForEvent(val);
          }
        }}>
          <SelectTrigger className="w-full sm:w-[280px]" data-testid="photo-event-filter">
            <Camera className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {eventGroups.map(g => (
              <SelectItem key={g.event_id} value={g.event_id}>
                {g.event_title} {g.event_date ? `(${g.event_date})` : ''} ({g.total_photos || g.photos.length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by event name or filename..."
            value={photoSearch}
            onChange={(e) => setPhotoSearch(e.target.value)}
            className="w-full pl-9 pr-3 h-10 rounded-md border border-input bg-background text-sm"
            data-testid="photo-search-input"
          />
        </div>
      </div>

      {/* Photo Grid - grouped by event */}
      {(selectedEvent === 'all' ? eventGroups : eventGroups.filter(g => g.event_id === selectedEvent))
        .filter(g => !photoSearch || g.event_title.toLowerCase().includes(photoSearch.toLowerCase()) || g.photos.some(p => p.filename?.toLowerCase().includes(photoSearch.toLowerCase())))
        .map(g => {
        const searchedPhotos = photoSearch
          ? g.photos.filter(p => g.event_title.toLowerCase().includes(photoSearch.toLowerCase()) || p.filename?.toLowerCase().includes(photoSearch.toLowerCase()))
          : g.photos;
        const displayPhotos = isTrial ? [] : searchedPhotos;
        const totalPhotos = g.total_photos || g.photos.length;
        const hasMore = g.photos.length < totalPhotos;
        return (
          <div key={g.event_id} className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg uppercase tracking-tight">
                {g.event_title} {g.event_date ? <span className="text-muted-foreground text-sm font-normal ml-2">{g.event_date}</span> : ''}
              </h3>
              <span className="text-sm text-muted-foreground">{totalPhotos} photos</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {displayPhotos.map((photo, index) => (
                <LazyImage
                  key={photo.id}
                  src={getThumbUrl(photo)}
                  alt={g.event_title}
                  className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                  onClick={() => openLightbox(photo, index, g)}
                  data-testid={`gallery-photo-${photo.id}`}
                />
              ))}
            </div>
            {hasMore && !isTrial && (
              <div className="mt-3 text-center">
                <Button variant="outline" size="sm" onClick={() => loadAllForEvent(g.event_id)} disabled={expandingEvent === g.event_id}
                  data-testid={`view-all-photos-${g.event_id}`}>
                  {expandingEvent === g.event_id ? 'Loading...' : `View All ${totalPhotos} Photos`}
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {/* Trial upgrade CTA */}
      {isTrial && (
        <div className="mt-6 p-6 bg-primary/5 border border-primary/20 rounded-lg text-center">
          <Lock className="w-8 h-8 text-primary mx-auto mb-2" />
          <p className="font-bold text-lg mb-1">Photos Not Available for Trial Users</p>
          <p className="text-sm text-muted-foreground mb-4">Subscribe to unlock all event photos</p>
          <Link to="/subscription">
            <Button size="lg">Subscribe Now</Button>
          </Link>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && currentPhoto && (
        <div 
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/50" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-white font-display font-bold text-lg uppercase tracking-tight" data-testid="lightbox-event-title">
                {lightboxEvent?.event_title}
              </h3>
              <p className="text-white/60 text-xs">{lightboxIndex + 1} of {lightboxPhotos.length}</p>
            </div>
            <div className="flex items-center gap-4">
              {!isExempt && (
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <Coins className="w-4 h-4" /> {userCredits} credits
                </span>
              )}
                <button 
                className="text-white/70 hover:text-white p-2"
                onClick={() => setLightboxOpen(false)}
                data-testid="lightbox-close-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Image with watermark overlay */}
          <div className="flex-1 flex items-center justify-center relative px-12" onClick={e => e.stopPropagation()}>
            {/* Nav arrows */}
            <button
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 bg-black/40 rounded-full"
              onClick={() => navigateLightbox(-1)}
              data-testid="lightbox-prev-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="relative select-none">
              <img
                src={fullPhotoUrl ? getPhotoUrl(fullPhotoUrl) : getPhotoUrl(currentPhoto.thumbnail_url || currentPhoto.url)}
                alt={currentPhoto.event_title}
                className="max-h-[calc(100vh-180px)] max-w-full object-contain"
                data-testid="lightbox-image"
                draggable="false"
                onContextMenu={e => { if (!isExempt) e.preventDefault(); }}
              />
              {/* Watermark overlay for non-exempt users */}
              {!isExempt && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ userSelect: 'none' }}>
                  <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-x-16 gap-y-10 -rotate-[25deg] scale-125 opacity-[0.18]">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <span key={i} className="text-white font-display font-bold text-2xl whitespace-nowrap tracking-widest uppercase">elevatedarena.com</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 bg-black/40 rounded-full"
              onClick={() => navigateLightbox(1)}
              data-testid="lightbox-next-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Actions Bar */}
          <div className="flex items-center justify-center gap-3 px-4 py-4 bg-black/50" onClick={e => e.stopPropagation()}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleDownload(currentPhoto)}
              data-testid="lightbox-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {isExempt ? 'Download' : `Download (${creditSettings?.photo_download_credits || 10} credits)`}
            </Button>

            {/* Mobile-only share button (uses native share API which opens Instagram, TikTok, Facebook, etc.) */}
            {'share' in navigator && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleShare('native', currentPhoto)}
                className="sm:hidden"
                data-testid="share-native-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ======================== EVENT VIDEOS TAB ========================
const EventVideosSection = ({ user }) => {
  const [eventGroups, setEventGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [expandingEvent, setExpandingEvent] = useState(null);
  const [videoSearch, setVideoSearch] = useState('');
  const [creditSettings, setCreditSettings] = useState(null);
  const [userCredits, setUserCredits] = useState(user?.credits || 0);
  const [mediaPricing, setMediaPricing] = useState(null);
  const [buyingVideoId, setBuyingVideoId] = useState(null); // which video is currently being checkout'd (loading state)
  const isSubscriber = user?.subscription_status === 'active' || user?.role === 'admin' || user?.role === 'coach';
  // Library content (event videos) is free for subscribers; credits are for critiques only
  const isExempt = user?.role === 'admin' || user?.role === 'coach' || user?.subscription_status === 'active';
  // Video DOWNLOADS: only admins/coaches skip payment. Subscribers pay too.
  const isDownloadExempt = user?.role === 'admin' || user?.role === 'coach';
  const isTrial = user?.subscription_status === 'trial';
  const TRIAL_LIMIT = 0;

  useEffect(() => { fetchVideos(); fetchCreditSettings(); fetchMediaPricing(); }, []);

  const fetchCreditSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/credit-settings`);
      setCreditSettings(res.data);
      setUserCredits(res.data.user_credits);
    } catch { /* ignore */ }
  };

  const fetchMediaPricing = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/media-pricing`, { headers });
      setMediaPricing(res.data);
    } catch { /* ignore */ }
  };

  // One-click purchase: POST checkout with user's known email, redirect to Stripe.
  const startVideoPurchase = async (video) => {
    if (!user?.email) {
      toast.error('Please log in to purchase');
      return;
    }
    setBuyingVideoId(video.id);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_URL}/api/videos/cart/checkout`, {
        video_ids: [video.id],
        customer_email: user.email,
        customer_name: user.name || '',
        origin_url: window.location.origin,
      }, { headers });
      // Full-page redirect to Stripe
      window.location.href = res.data.url;
    } catch (err) {
      console.error('Video purchase error:', err);
      toast.error(err.response?.data?.detail || err.message || 'Checkout failed');
      setBuyingVideoId(null);
    }
  };

  useEffect(() => { fetchVideos(); }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/event-videos?per_event=6`);
      setEventGroups(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch event videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllForEvent = async (eventId) => {
    try {
      setExpandingEvent(eventId);
      const res = await axios.get(`${API_URL}/api/event-videos?event_id=${eventId}&limit=500`);
      const fullEvent = (res.data.events || [])[0];
      if (fullEvent) {
        setEventGroups(prev => prev.map(g => g.event_id === eventId ? { ...g, videos: fullEvent.videos, total_videos: fullEvent.total_videos } : g));
      }
    } catch (err) {
      console.error('Failed to load all videos for event:', err);
    } finally {
      setExpandingEvent(null);
    }
  };

  const getVideoUrl = (url) => url?.startsWith('/uploads') ? `${API_URL}${url}` : url;

  const isExternalVideo = (video) => {
    if (video.video_type === 'external') return true;
    const url = video.url || '';
    return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  const handleShare = (video) => {
    const pageUrl = window.location.origin + '/library';
    const text = `Check out this video from ${video.event_title} - Elevated Roping Media`;
    if (navigator.share) {
      navigator.share({ title: video.event_title, text, url: pageUrl }).catch(() => {});
    }
  };

  if (!isSubscriber && !isTrial) {
    return (
      <div className="text-center py-16">
        <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-2">Subscribers Only</h3>
        <p className="text-muted-foreground mb-6">Subscribe to view and download event videos.</p>
        <Link to="/subscription">
          <Button size="lg" data-testid="subscribe-for-videos-btn">Get Access</Button>
        </Link>
      </div>
    );
  }

  if (loading) return <div className="flex justify-center py-16"><div className="spinner" /></div>;

  if (eventGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <Film className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-semibold text-lg mb-2">No event videos yet</h3>
        <p className="text-muted-foreground">Videos from events will appear here.</p>
      </div>
    );
  }

  return (
    <>
      {/* Event Filter & Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <Select value={selectedEvent} onValueChange={(val) => {
          setSelectedEvent(val);
          if (val !== 'all') {
            loadAllForEvent(val);
          }
        }}>
          <SelectTrigger className="w-full sm:w-[280px]" data-testid="video-event-filter">
            <Film className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {eventGroups.map(g => (
              <SelectItem key={g.event_id} value={g.event_id}>
                {g.event_title} {g.event_date ? `(${g.event_date})` : ''} ({g.total_videos || g.videos.length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by event name or filename..."
            value={videoSearch}
            onChange={(e) => setVideoSearch(e.target.value)}
            className="w-full pl-9 pr-3 h-10 rounded-md border border-input bg-background text-sm"
            data-testid="video-search-input"
          />
        </div>
      </div>

      {/* Video Grid - grouped by event */}
      {(selectedEvent === 'all' ? eventGroups : eventGroups.filter(g => g.event_id === selectedEvent))
        .filter(g => !videoSearch || g.event_title.toLowerCase().includes(videoSearch.toLowerCase()) || g.videos.some(v => v.filename?.toLowerCase().includes(videoSearch.toLowerCase())))
        .map(g => {
        const searchedVideos = videoSearch
          ? g.videos.filter(v => g.event_title.toLowerCase().includes(videoSearch.toLowerCase()) || v.filename?.toLowerCase().includes(videoSearch.toLowerCase()))
          : g.videos;
        const displayVideos = isTrial ? [] : searchedVideos;
        const totalVideos = g.total_videos || g.videos.length;
        const hasMore = g.videos.length < totalVideos;
        return (
          <div key={g.event_id} className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg uppercase tracking-tight">
                {g.event_title} {g.event_date ? <span className="text-muted-foreground text-sm font-normal ml-2">{g.event_date}</span> : ''}
              </h3>
              <span className="text-sm text-muted-foreground">{totalVideos} videos</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayVideos.map(video => (
                <div
                  key={video.id}
                  className="rounded-lg overflow-hidden border bg-card group"
                  data-testid={`gallery-video-${video.id}`}
                >
                  <div
                    className="relative aspect-video bg-black cursor-pointer"
                    onClick={() => setPlayingVideo({...video, event_title: g.event_title})}
                  >
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.filename} className="w-full h-full object-cover" />
                    ) : isExternalVideo(video) ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                        <ExternalLink className="w-10 h-10 text-white/40" />
                      </div>
                    ) : (
                      <video src={getVideoUrl(video.url)} preload="metadata" muted playsInline
                        className="w-full h-full object-cover pointer-events-none"
                        onLoadedMetadata={(e) => { e.target.currentTime = Math.min(3, e.target.duration * 0.25); }} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                      <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-7 h-7 text-primary ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium truncate">{video.filename}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">{g.event_title} {g.event_date ? `· ${g.event_date}` : ''} · {video.size_mb} MB</span>
                      {!isExternalVideo(video) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isDownloadExempt) {
                              // Admin/coach — direct free download
                              const a = document.createElement('a');
                              a.href = `${API_URL}/api/event-videos/download/${video.id}`;
                              a.download = video.filename;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            } else {
                              // Everyone else — straight to Stripe (one click)
                              startVideoPurchase({ ...video, event_title: g.event_title });
                            }
                          }}
                          disabled={buyingVideoId === video.id}
                          className="flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-wait"
                          data-testid={`download-video-${video.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          {isDownloadExempt
                            ? 'Download'
                            : buyingVideoId === video.id
                              ? 'Redirecting…'
                              : `Buy · $${(mediaPricing?.video_gallery_price ?? 10).toFixed(2)}`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {hasMore && !isTrial && (
              <div className="mt-3 text-center">
                <Button variant="outline" size="sm" onClick={() => loadAllForEvent(g.event_id)} disabled={expandingEvent === g.event_id}
                  data-testid={`view-all-videos-${g.event_id}`}>
                  {expandingEvent === g.event_id ? 'Loading...' : `View All ${totalVideos} Videos`}
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {/* Trial upgrade CTA */}
      {isTrial && (
        <div className="mt-6 p-6 bg-primary/5 border border-primary/20 rounded-lg text-center">
          <Lock className="w-8 h-8 text-primary mx-auto mb-2" />
          <p className="font-bold text-lg mb-1">Videos Not Available for Trial Users</p>
          <p className="text-sm text-muted-foreground mb-4">Subscribe to unlock all event videos</p>
          <Link to="/subscription">
            <Button size="lg">Subscribe Now</Button>
          </Link>
        </div>
      )}

      {/* Video Player Lightbox */}
      {playingVideo && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={() => setPlayingVideo(null)}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/50" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-white font-display font-bold text-lg uppercase tracking-tight" data-testid="video-lightbox-title">
                {playingVideo.event_title}
              </h3>
              <p className="text-white/60 text-xs">{playingVideo.filename}</p>
            </div>
            <button
              className="text-white/70 hover:text-white p-2"
              onClick={() => setPlayingVideo(null)}
              data-testid="video-lightbox-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Video Player */}
          <div className="flex-1 flex items-center justify-center px-4 relative" onClick={e => e.stopPropagation()}>
            {isExternalVideo(playingVideo) ? (
              <iframe
                src={getEmbedUrl(playingVideo.url)}
                className="w-full max-w-4xl aspect-video rounded"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write"
                allowFullScreen
                data-testid="event-video-iframe"
              />
            ) : (
              <video
                src={getVideoUrl(playingVideo.url)}
                controls
                autoPlay
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                className="max-h-[calc(100vh-160px)] max-w-full"
                data-testid="event-video-player"
              />
            )}
            {/* Watermark overlay for non-exempt users */}
            {!isExempt && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ userSelect: 'none' }}>
                <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-x-16 gap-y-10 -rotate-[25deg] scale-125 opacity-[0.15]">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <span key={i} className="text-white font-display font-bold text-2xl whitespace-nowrap tracking-widest uppercase">elevatedarena.com</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions Bar */}
          <div className="flex items-center justify-center gap-3 px-4 py-4 bg-black/50" onClick={e => e.stopPropagation()}>
            {!isExternalVideo(playingVideo) && (
              <button
                onClick={() => {
                  if (isDownloadExempt) {
                    // Admin/coach — direct free download
                    const a = document.createElement('a');
                    a.href = `${API_URL}/api/event-videos/download/${playingVideo.id}`;
                    a.download = playingVideo.filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } else {
                    // Everyone else — straight to Stripe
                    const v = playingVideo;
                    setPlayingVideo(null);
                    startVideoPurchase(v);
                  }
                }}
                disabled={buyingVideoId === playingVideo.id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-wait"
                data-testid="video-download-btn"
              >
                <Download className="w-4 h-4" />
                {isDownloadExempt
                  ? 'Download'
                  : buyingVideoId === playingVideo.id
                    ? 'Redirecting…'
                    : `Buy · $${(mediaPricing?.video_gallery_price ?? 10).toFixed(2)}`}
              </button>
            )}
            {/* Mobile-only share */}
            {'share' in navigator && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleShare(playingVideo)}
                className="sm:hidden"
                data-testid="video-share-native-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ======================== LIVE RECORDINGS TAB ========================
const LiveRecordingsSection = ({ user }) => {
  const [eventGroups, setEventGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [videoSearch, setVideoSearch] = useState('');
  const [creditSettings, setCreditSettings] = useState(null);
  const [userCredits, setUserCredits] = useState(user?.credits || 0);
  const isSubscriber = user?.subscription_status === 'active' || user?.role === 'admin' || user?.role === 'coach';
  const isExempt = user?.role === 'admin' || user?.role === 'coach';
  const isTrial = user?.subscription_status === 'trial';

  useEffect(() => {
    const fetchLiveRecordings = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/event-videos/live-recordings`);
        setEventGroups(res.data.events || []);
      } catch (err) {
        console.error('Failed to fetch live recordings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLiveRecordings();
    const fetchCredits = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/credit-settings`);
        setCreditSettings(res.data);
        setUserCredits(res.data.user_credits);
      } catch { /* ignore */ }
    };
    fetchCredits();
  }, []);

  const getVideoUrl = (url) => url?.startsWith('/uploads') ? `${API_URL}${url}` : url;
  const isExternalVideo = (video) => {
    const url = video.url || '';
    return url.includes('youtube') || url.includes('youtu.be') || url.includes('vimeo') || url.includes('dartfish') || video.video_type === 'external';
  };

  if (loading) return <div className="flex justify-center py-16"><div className="spinner" /></div>;

  if (eventGroups.length === 0) {
    return (
      <div className="text-center py-16">
        <Radio className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-display font-bold text-xl uppercase mb-2">No Live Recordings Yet</h3>
        <p className="text-muted-foreground">Previously recorded live streams will appear here.</p>
      </div>
    );
  }

  return (
    <>
      {!isSubscriber && !isTrial && (
        <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg text-center">
          <Lock className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="font-bold mb-1">Subscribe to Watch Live Recordings</p>
          <Link to="/subscription"><Button size="sm">Subscribe Now</Button></Link>
        </div>
      )}

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search recordings..."
            value={videoSearch}
            onChange={(e) => setVideoSearch(e.target.value)}
            className="w-full pl-9 pr-3 h-10 rounded-md border border-input bg-background text-sm"
            data-testid="live-search-input"
          />
        </div>
      </div>

      {/* Recordings grouped by event */}
      {eventGroups
        .filter(g => !videoSearch || g.event_title.toLowerCase().includes(videoSearch.toLowerCase()) || g.videos.some(v => v.filename?.toLowerCase().includes(videoSearch.toLowerCase())))
        .map(g => {
        const searchedVideos = videoSearch
          ? g.videos.filter(v => g.event_title.toLowerCase().includes(videoSearch.toLowerCase()) || v.filename?.toLowerCase().includes(videoSearch.toLowerCase()))
          : g.videos;
        const displayVideos = isTrial ? [] : searchedVideos;
        return (
          <div key={g.event_id} className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg uppercase tracking-tight flex items-center gap-2">
                <Radio className="w-5 h-5 text-red-500" />
                {g.event_title}
                {g.event_date && <span className="text-muted-foreground text-sm font-normal ml-2">{g.event_date}</span>}
              </h3>
              <span className="text-sm text-muted-foreground">{g.total_videos} recording{g.total_videos !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayVideos.map(video => (
                <div
                  key={video.id}
                  className="rounded-lg overflow-hidden border bg-card group"
                  data-testid={`live-video-${video.id}`}
                >
                  <div
                    className="relative aspect-video bg-black cursor-pointer"
                    onClick={() => setPlayingVideo({...video, event_title: g.event_title})}
                  >
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.filename} className="w-full h-full object-cover" />
                    ) : isExternalVideo(video) ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-900 to-gray-900">
                        <Radio className="w-10 h-10 text-white/40" />
                      </div>
                    ) : (
                      <video src={getVideoUrl(video.url)} preload="metadata" muted playsInline
                        className="w-full h-full object-cover pointer-events-none"
                        onLoadedMetadata={(e) => { e.target.currentTime = Math.min(3, e.target.duration * 0.25); }} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                      <div className="w-14 h-14 rounded-full bg-red-500/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-7 h-7 text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute top-2 left-2">
                      <Badge variant="destructive" className="text-xs">RECORDED LIVE</Badge>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium truncate">{video.filename}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">{g.event_date || ''} · {video.size_mb} MB</span>
                      {!isExternalVideo(video) && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!isExempt) {
                              const cost = creditSettings?.video_download_credits || 20;
                              if (userCredits < cost) {
                                toast.error(`Not enough credits. Need ${cost}, you have ${userCredits}.`);
                                return;
                              }
                              try {
                                const res = await axios.post(`${API_URL}/api/library/download-video/${video.id}`);
                                setUserCredits(res.data.credits_remaining);
                                toast.success(`${res.data.credits_used} credits used. ${res.data.credits_remaining} remaining.`);
                                const a = document.createElement('a');
                                a.href = `${API_URL}${res.data.download_url}`;
                                a.download = video.filename;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Download failed');
                              }
                            } else {
                              const a = document.createElement('a');
                              a.href = `${API_URL}/api/event-videos/download/${video.id}`;
                              a.download = video.filename;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            }
                          }}
                          className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {isExempt ? 'Download' : `${creditSettings?.video_download_credits || 20} cr`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {isTrial && (
        <div className="mt-6 p-6 bg-primary/5 border border-primary/20 rounded-lg text-center">
          <Lock className="w-8 h-8 text-primary mx-auto mb-2" />
          <p className="font-bold text-lg mb-1">Live Recordings Not Available for Trial Users</p>
          <p className="text-sm text-muted-foreground mb-4">Subscribe to watch all recorded live streams</p>
          <Link to="/subscription"><Button size="lg">Subscribe Now</Button></Link>
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setPlayingVideo(null)}>
          <div className="w-full max-w-5xl mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold">{playingVideo.filename}</h3>
                <p className="text-white/60 text-sm">{playingVideo.event_title}</p>
              </div>
              <button onClick={() => setPlayingVideo(null)} className="p-2 text-white/60 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
              {isExternalVideo(playingVideo) ? (
                playingVideo.url?.includes('youtube') || playingVideo.url?.includes('youtu.be') ? (
                  <iframe src={`https://www.youtube.com/embed/${playingVideo.url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1] || ''}?autoplay=1`} className="w-full h-full" allowFullScreen allow="autoplay" frameBorder="0" />
                ) : playingVideo.url?.includes('vimeo') ? (
                  <iframe src={`https://player.vimeo.com/video/${playingVideo.url.match(/vimeo\.com\/(\d+)/)?.[1] || ''}?autoplay=1`} className="w-full h-full" allowFullScreen allow="autoplay" frameBorder="0" />
                ) : (
                  <iframe src={playingVideo.url} className="w-full h-full" allowFullScreen frameBorder="0" />
                )
              ) : (
                <video src={getVideoUrl(playingVideo.url)} controls autoPlay controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} className="w-full h-full" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ======================== MAIN LIBRARY PAGE ========================
const LibraryPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-primary" />
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
              Learning Library
            </h1>
          </div>
          <p className="text-muted-foreground">
            Training videos and event photos to help you improve your roping skills.
          </p>
        </div>

        {/* Tabs: Videos | Event Videos | Live Recordings — photos are now in /gallery (purchase) */}
        <Tabs defaultValue="videos" className="space-y-6">
          <TabsList data-testid="library-tabs" className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="videos" className="flex items-center gap-2" data-testid="library-videos-tab">
              <Video className="w-4 h-4" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="event-videos" className="flex items-center gap-2" data-testid="library-event-videos-tab">
              <Film className="w-4 h-4" />
              Event Videos
            </TabsTrigger>
            <TabsTrigger value="live-recordings" className="flex items-center gap-2" data-testid="library-live-tab">
              <Radio className="w-4 h-4" />
              Live Event Recordings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="videos">
            <VideosSection user={user} />
          </TabsContent>

          <TabsContent value="event-videos">
            <EventVideosSection user={user} />
          </TabsContent>

          <TabsContent value="live-recordings">
            <LiveRecordingsSection user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LibraryPage;
